/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * 权益管理器 - Composition API 风格的单例实现
 * 职责：
 * 1. 管理权益卡片的上下文数据
 * 2. 控制权益卡片的显示/隐藏
 * 3. 提供简单的更新和重置方法
 * 4. 集成埋点功能
 */
import { BaseAgreement, ForceAgreement } from '@hb/coffee-widgets-agreement';
import { EquityPathType } from '@/api/apply-product/types';
import { RightPackageSourceEnum, RightPackageTypeEnum } from '@/api/rights/types';
import { EquitySelectedEnum, PricingTypeEnum } from '@/enums/rights';
import ProductCommodity from '@/shared/commodity';
import { ref, unref } from '@vue/composition-api';
import { getSummaryDataHandler } from '@/views_v2/apply-product/components/rights/zhenxiang-card/utils';
import router from '@/router';
import { RightTipsDialogActionEnum, RightTipsDialogSceneEnum } from '@/views_v2/apply-product/components/rights/zhenxiang-card/enum';
import { Toast } from '@hb/coffee';
import { calcAutarkyNeedRight, calcUnionChannelNeedRight } from './utils/calcNeedRight';
import type { RightsContext, RightsDataType } from './types';
import { RightDataDialog } from './utils/rightTipsDialog';
import { DefaultInitStateEnum } from './types';
import { createTrackingManager } from './utils/tracking';
import { destroyDetailPopup } from './utils/detailPopupService';

/**
 * 创建权益管理器
 * 使用 Composition API 风格，避免 this 的使用
 */
const createRightsManager = () => {
  // ===== 响应式状态 =====
  const context = ref<Partial<RightsContext>>({
    showSpeed: true,
    checkStatus: false,
    detailShow: false,
    contractShow: false,
    initState: DefaultInitStateEnum.DEFAULT,
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const shouldShowValue = ref<boolean>(false);

  // ===== 埋点管理器 =====
  // 默认页面ID，可以通过 setPageId 方法更新
  let trackingManager = createTrackingManager(router.currentRoute.meta?.pageId);

  // ===== 私有辅助方法 =====

  /**
   * 检查是否是自营产品
   */
  const isAutarky = (): boolean => (
    context.value.channel?.productId === ProductCommodity.AUTARKY_B_COMMODITY
  );

  /**
   * 检查是否为双承接模式，即当前资方列表是否返回两个
   */
  const isDoubleIntegrateMode = (): boolean => {
    const ctx = context.value as RightsContext;
    if (ctx.channel) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { capitalList = [] } = ctx.channel || {};
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return capitalList.length > 1;
    }
    return false;
  };

  /**
   * 判断是否可以提交订单（购买权益）
   */
  const canSubmitOrder = (): boolean => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const needRight = shouldShowValue.value as boolean;
    const { showSpeed } = context.value;
    // 需要权益 && 显示加速通道
    return needRight && !!showSpeed;
  };

  /**
   * 计算权益路径枚举值
   * 参考: hello-app/index.tsx 中的 equatyValue getter
   */
  const calculateEquityPathValue = (): EquityPathType => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const needRight = shouldShowValue.value as boolean;
    const isPricingType24MustRightsOr36 = context.value?.planListData?.pricingType
    === PricingTypeEnum.LOAN_24_MUST_RIGHTS_OR_LOAN_36;

    // 存在该字段且等于false，传2
    if (needRight === false) {
      if (isAutarky()) {
        return EquityPathType.NOT_NEED_RIGHT;
      }
      // 联营产品 - needRight === false,  equatyValue = EquityPathType.NONE
      return isPricingType24MustRightsOr36 ? EquityPathType.NOT_NEED_RIGHT : EquityPathType.NONE;
    }

    // 根据是否购买权益，返回不同的权益路径枚举值
    if (isAutarky()) {
      const isDouble = isDoubleIntegrateMode();
      if (isDouble) {
        return canSubmitOrder() ? EquityPathType.BUY_RIGHTS : EquityPathType.NOT_NEED_RIGHT;
      }
      return canSubmitOrder() ? EquityPathType.BUY_RIGHTS : EquityPathType.NORMAL;
    }

    if (canSubmitOrder()) {
      return EquityPathType.BUY_RIGHTS;
    }
    // 联营产品：根据 pricingType 字段判断
    return isPricingType24MustRightsOr36
      ? EquityPathType.NOT_NEED_RIGHT
      : EquityPathType.NORMAL;
  };

  const getSummaryData = async (): Promise<void> => {
    try {
      const summaryData = await getSummaryDataHandler({
        productId: context.value.channel?.productId as string,
        orderAmount: Number(context.value.amount) || 0,
        period: Number(context.value.period) || 0,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        capitalCode: context.value?.capitalCode || '',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        pricingType: context.value?.planListData?.pricingType || undefined,
        packageType: RightPackageTypeEnum.CHANNEL_B,
        rateValue: context.value.planListData?.rateValue || undefined,
      });
      // ✅ 通过替换整个对象触发响应式更新
      context.value = {
        ...context.value,
        rightSummaryData: summaryData,
      };
    } catch (error) {
      RightDataDialog.showDialog(
        RightTipsDialogSceneEnum.NET_ERROR,
        RightTipsDialogActionEnum.REFRESH_RIGHTS_DATA,
        () => {
          // 点击刷新按钮后重新请求一次数据
          Toast.loading({
            duration: 0,
            forbidClick: true,
            message: '信息获取中，请耐心等待',
          });
          // 调用store中的刷新方法
          getSummaryData();
        },
      );
      context.value = {
        ...context.value,
        rightSummaryData: undefined,
      };
    }
  };

  /**
   * 计算是否应该显示权益卡片
   * 1、自营8180根据needRight字段判断
   * 2、联营产品需要调用接口来获取是否需要权益规则
   */
  const calculateShouldShow = async (): Promise<void> => {
    // 上下文为空时不显示
    if (Object.keys(context.value).length === 0 || !context.value.channel) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      shouldShowValue.value = false;
      return;
    }

    const { channel, planListData, amount } = context.value as RightsContext;

    // 自营8180产品：根据 needRight 字段判断
    if (isAutarky() && channel) {
      const { needRight } = calcAutarkyNeedRight(channel);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      shouldShowValue.value = !!needRight;
      if (needRight) {
        // 调用该要接口，保证获取到是否为卖量外部闭环的卡种，保证视图分发逻辑正常
        await getSummaryData();
      }
      return;
    }

    // 联营产品：调用接口判断
    const result = await calcUnionChannelNeedRight({
      packageType: RightPackageTypeEnum.CHANNEL_B,
      productId: (channel?.productId || ''),
      applyAmount: amount,
      source: RightPackageSourceEnum.LOAN,
      rateValue: planListData?.rateValue,
      pricingType: planListData?.pricingType,
    });
    if (result?.needRight) {
      // 调用该要接口，保证获取到是否为卖量外部闭环的卡种，保证视图分发逻辑正常
      await getSummaryData();
    } else {
      context.value = {
        ...context.value,
        showSpeed: false,
      };
    }
    // 标记初始化完成
    context.value = {
      ...context.value,
      initState: DefaultInitStateEnum.HAS_INIT,
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    shouldShowValue.value = !!(result?.needRight);
  };

  // ===== 公共方法 =====

  /**
   * 更新上下文
   * ✅ 通过创建新对象触发响应式更新
   * ✅ 自动计算 shouldShow
   */
  const updateContext = async (
    newContext: Partial<RightsContext>,
    shouldCalcNeedRight = true,
  ): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    context.value = {
      ...context.value,
      ...newContext,
    };

    if (shouldCalcNeedRight) {
      await calculateShouldShow();
    }
  };

  /**
   * 重置上下文
   * ✅ 通过替换为空对象触发响应式更新
   */
  const reset = (): void => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    context.value = {};
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    shouldShowValue.value = false;
  };

  /**
   * 验证勾选状态,返回是否可以继续走提现流程
   * 1、showSpeed为true，checkStatus为true，返回true
   * 2、showSpeed为false，返回true
   */
  const validate = (): Promise<boolean> => {
    if (context.value) {
      const { showSpeed } = context.value;
      if (showSpeed
        // 未勾选才提示
        && !context.value?.checkStatus
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        && unref(shouldShowValue.value)) {
        // 通知显示tips，并且滚动到可视区
        updateContext({
          tipShow: true,
        }, false);
        return Promise.resolve(!!context.value?.checkStatus);
      }
      return Promise.resolve(true);
    }
    return Promise.resolve(true);
  };

  /**
   * 获取权益数据
   */
  const getData = (): Promise<RightsDataType> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const needRight = shouldShowValue.value as boolean;
    const contextValue = context.value as RightsContext;

    /** 权益路径枚举值（用于提交订单） */
    const equityPath = calculateEquityPathValue();
    /** 状态值 */
    const statusValue = {
      showSpeed: contextValue.showSpeed,
      checkStatus: contextValue.checkStatus,
      detailShow: contextValue.detailShow || false,
      contractShow: contextValue.contractShow || false,
    };

    const commonParams = {
      equityPath,
      packageType: RightPackageTypeEnum.CHANNEL_B,
      packageFrom: contextValue.from,
      pricingType: contextValue.planListData?.pricingType || undefined,
    };

    /** 不需要提交订单，返回权益路径枚举值和状态值 */
    if (!canSubmitOrder()) {
      return Promise.resolve({
        loanApplyMarketReq: {
          ...commonParams,
        },
        status: { ...statusValue },
      });
    }

    return Promise.resolve({
      loanApplyMarketReq: {
        packageId: contextValue.rightSummaryData?.packageId || '',
        /** 权益路径枚举值（用于提交订单） */
        /** 权益下单价格,臻享卡特有，主要作用于用户命中权益豁免的情况下，后端用于下单校验 */
        expectPrice: contextValue.rightSummaryData?.sellPrice || 0,
        ...commonParams,
      },
      status: { ...statusValue },
    });
  };

  /**
   * 获取权益选择状态,主要用于给试算的接口调用，用于交互轻资产产品是否需要权益
   */
  const getEquitySelected = (): EquitySelectedEnum | undefined => {
    /* eslint-disable @typescript-eslint/no-unsafe-member-access */
    /* eslint-disable @typescript-eslint/no-unsafe-assignment */
    const shouldShow = shouldShowValue.value;
    /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    /* eslint-enable @typescript-eslint/no-unsafe-assignment */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (shouldShow === undefined) {
      return undefined;
    }

    return context.value?.showSpeed ? EquitySelectedEnum.YES : EquitySelectedEnum.NO;
  };

  /**
   * 设置页面ID（用于埋点）
   */
  const setPageId = (pageId: string): void => {
    trackingManager = createTrackingManager(pageId);
  };

  /**
   * 获取埋点管理器
   */
  const getTrackingManager = () => trackingManager;

  /**
   * 销毁所有弹窗实例
   * 包括详情弹窗和协议弹窗
   */
  const destroyInstance = (): void => {
    // 销毁详情弹窗
    destroyDetailPopup();

    // 销毁协议弹窗（清除所有协议弹窗实例）
    BaseAgreement.clear();
    ForceAgreement.clear();
    updateContext({
      detailShow: false,
      contractShow: false,
    }, false);
  };

  // ===== 返回公共 API =====
  return {
    // 响应式状态（只读）
    context,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    shouldShowValue,

    // 公共方法
    updateContext,
    reset,
    validate,
    getData,
    getEquitySelected,
    getSummaryData,

    // 埋点相关
    setPageId,
    getTrackingManager,

    // 销毁相关
    destroyInstance,
  };
};

// 导出单例
export const rightsManager = createRightsManager();

// 开发环境暴露到 window 方便调试
if (process.env.NODE_ENV === 'development') {
  (window as unknown as Record<string, unknown>).__rightsManager = rightsManager;
}
